/**
 * LandingNav — MADE-260
 *
 * Minimal nav for the Made.bb landing page only.
 * Not used on /shop/* pages (those use ShopNav).
 *
 * Links: Shop · Custom Order
 * Staff Login lives in LandingFooter — not here.
 */

import { useState } from 'react';
import {
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

interface NavLink {
  label: string;
  href: string;
}

const NAV_LINKS: NavLink[] = [
  { label: 'Shop', href: '/shop' },
  { label: 'Custom Order', href: '/shop/contact' },
];

export function LandingNav() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpen = () => setDrawerOpen(true);
  const handleClose = () => setDrawerOpen(false);

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 2, md: 6 },
        py: 1.5,
        backgroundColor: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(8px)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
      }}
    >
      {/* Logo */}
      <Box
        component="a"
        href="/madebb/"
        aria-label="Made.bb — home"
        sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
      >
        <Box
          component="img"
          src="/madebb/logo.png"
          alt="Made.bb"
          sx={{ height: 156, width: 'auto' }}
        />
      </Box>

      {/* Desktop links */}
      <Box
        component="ul"
        sx={{
          display: { xs: 'none', md: 'flex' },
          gap: 4,
          listStyle: 'none',
          m: 0,
          p: 0,
          alignItems: 'center',
        }}
      >
        {NAV_LINKS.map(({ label, href }) => (
          <Box component="li" key={href}>
            <Box
              component="a"
              href={href}
              sx={{
                textDecoration: 'none',
                color: '#2c3e50',
                fontWeight: 500,
                fontSize: '0.95rem',
                letterSpacing: '0.02em',
                transition: 'color 0.2s ease',
                '&:hover': { color: '#667eea' },
              }}
            >
              {label}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Mobile hamburger */}
      <IconButton
        aria-label="Open navigation menu"
        onClick={handleOpen}
        sx={{ display: { xs: 'flex', md: 'none' }, color: '#2c3e50' }}
      >
        <MenuIcon />
      </IconButton>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleClose}
        PaperProps={{ sx: { width: 260 } }}
      >
        <Box
          sx={{
            pt: 1.5,
            pb: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <Box
            component="img"
            src="/madebb/logo.png"
            alt="Made.bb"
            sx={{ height: 120, width: 'auto' }}
          />
          <IconButton aria-label="Close navigation menu" onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ pt: 2 }}>
          {NAV_LINKS.map(({ label, href }) => (
            <ListItem key={href} disablePadding>
              <ListItemButton
                sx={{ py: 1.5, px: 3 }}
                onClick={() => {
                  handleClose();
                  window.location.href = href;
                }}
              >
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    color: '#2c3e50',
                    fontSize: '1rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </Box>
  );
}
